import Box from '@ramsey-design-system/box';
import Button from '@ramsey-design-system/button';
import { ArrowRightIcon } from '@ramsey-design-system/icons';
import { SubmitEvent } from 'react';
import { useWizard } from 'react-use-wizard';
import { useGAFormTracking } from '../../../../../shared/hooks';
import {
  trackElementClicked,
  trackStepCompleted
} from '../../../../../shared/analytics';
import { appendSyntheticMonitorParam } from '../../../../../shared/utils';
import { useReferralContext } from '../../../context';
import {
  buildRamseyConnectChatUrl,
  getAdviceTopicContent,
  getFormName,
  useTrackStepStarted
} from '../../../utils';
import { BackChevron } from '../../BackChevron';

const STEP_NAME = 'ramsey_connect_advice_step';
const STEP_LABEL = 'Ramsey Connect Advice Step';
const CHAT_INPUT_ELEMENT = 'ask_ramsey_chat_input_button';
const CHAT_BUTTON_ELEMENT = 'go_to_ask_ramsey_button';
const CONTINUE_ELEMENT = 'continue_search_for_a_pro_button';
const CONTINUE_LABEL = 'Continue Search for a Pro';

function RamseyConnectAdviceStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { nextStep, activeStep } = useWizard();
  const { referral } = useReferralContext();

  const topicContent = getAdviceTopicContent(referral.tax_advice);

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  function trackClick(element: string, label: string) {
    trackElementClicked({
      element,
      label,
      page: STEP_NAME,
      referral_key: 'n/a'
    });
  }

  async function onValidSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    trackClick(CONTINUE_ELEMENT, CONTINUE_LABEL);
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: [CONTINUE_ELEMENT],
      answer_values: [CONTINUE_ELEMENT],
      form_name: getFormName()
    });
    await nextStep();
  }

  if (!topicContent) {
    return null;
  }

  const { heading, question, utmContent } = topicContent;
  const chatUrl = appendSyntheticMonitorParam(
    buildRamseyConnectChatUrl(question, utmContent)
  );

  return (
    <div data-testid={'step-wrapper'} className="flex justify-center">
      <Box
        className="w-[345px] sm:!w-[418px]"
        hasBorder={true}
        padding="lg"
        color="white"
      >
        {/* Kept in normal flow, matching StepWrapper. Absolute positioning
            here would let the heading run under the chevron, since BackChevron
            pulls itself in with a negative margin and so contributes almost no
            height of its own. */}
        <div className="flex items-center mb-4">
          <BackChevron dataTestID="tax_referral_form_back_button" />
        </div>
        <form ref={formRef} onSubmit={onValidSubmit}>
          <p className="text-6 sm:text-7 font-semibold mb-0.5 text-center text-gray-90">
            {heading}
          </p>
          <p className="text-3 mb-4 sm:text-4 text-center">
            Get your tax answers fast,
            <br></br>
            built on Ramsey principles.
          </p>
          <a
            href={chatUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={CHAT_INPUT_ELEMENT}
            onClick={() => trackClick(CHAT_INPUT_ELEMENT, question)}
            className="ask-ramsey-chat-input flex items-center justify-between gap-2 mb-4 px-3 py-2 no-underline text-left text-gray-90 text-3 sm:text-4"
          >
            {question}
            <ArrowRightIcon className="fill-blue-50 h-2 sm:h-[18px] w-auto shrink-0" />
          </a>
          <Button
            element="a"
            href={chatUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={CHAT_BUTTON_ELEMENT}
            onClick={() => trackClick(CHAT_BUTTON_ELEMENT, question)}
            icon={ArrowRightIcon}
            iconPosition="right"
            className="!w-full"
            fullWidthOnMobile={true}
            size="large"
          >
            Go to Ramsey Connect
          </Button>
          <div className="text-center mt-2">
            <Button
              type="submit"
              appearance="subtle"
              size="large"
              className="!w-full"
              fullWidthOnMobile={true}
              data-testid={CONTINUE_ELEMENT}
            >
              Continue Search for a Pro
            </Button>
          </div>
        </form>
      </Box>
    </div>
  );
}

export { RamseyConnectAdviceStep };
